<script setup>
import { ref, shallowRef, onUnmounted, watch } from 'vue'
import { useAuthStore } from '~/stores/auth.js'
import { useAuthApi } from '~/composables/api/useAuthApi.js'
import { useUpload } from '~/composables/useUpload.js'

const ERROR_AUTO_DISMISS_MS = 6000

const props = defineProps({
  onSubmit: Function,
  onCancel: Function,
  redirect: { type: String, default: '/dashboard' },
})

const auth = useAuthStore()
const authApi = useAuthApi()
const upload = useUpload()
const toast = useToast()
const { t } = useI18n()
const googleAuth = useGoogleAuth()

const mode = ref('login')

const email = ref('')
const password = ref('')
const loading = ref(false)
const error = ref('')

const forgotEmail = ref('')
const forgotLoading = ref(false)
const forgotError = ref('')

let errorTimer = null
const showError = (msg) => {
  error.value = msg
  if (errorTimer) clearTimeout(errorTimer)
  errorTimer = setTimeout(() => {
    error.value = ''
    errorTimer = null
  }, ERROR_AUTO_DISMISS_MS)
}
const clearError = () => {
  error.value = ''
  if (errorTimer) {
    clearTimeout(errorTimer)
    errorTimer = null
  }
}
onUnmounted(() => {
  if (errorTimer) clearTimeout(errorTimer)
})

const close = () => props.onCancel?.()

const goForgot = () => {
  mode.value = 'forgot'
  forgotEmail.value = email.value
  forgotError.value = ''
}

const goLogin = () => {
  mode.value = 'login'
  forgotError.value = ''
}

const onLogin = async () => {
  if (loading.value) return
  if (!email.value || !password.value) {
    showError(t('modal.login.errors.missing_fields'))
    return
  }
  loading.value = true
  clearError()
  try {
    const payload = await authApi.login(email.value, password.value)
    await auth.login(payload)
    await props.onSubmit?.({ email: email.value, password: password.value })
    await navigateTo(props.redirect)
  } catch (e) {
    const code = e?.message
    const map = {
      invalid_credentials: 'invalid_credentials',
      customer_disabled:   'customer_disabled',
    }
    showError(t(`modal.login.errors.${map[code] || 'generic'}`))
  } finally {
    loading.value = false
  }
}

const onForgotSubmit = async () => {
  if (forgotLoading.value) return
  const value = forgotEmail.value.trim()
  if (!value) {
    forgotError.value = t('modal.login.forgot_view.errors.missing_email')
    return
  }
  forgotLoading.value = true
  forgotError.value = ''
  try {
    await authApi.requestPasswordReset(value)
    toast.success(t('modal.login.forgot_view.success'))
    goLogin()
  } catch (e) {
    forgotError.value = t('modal.login.forgot_view.errors.generic')
  } finally {
    forgotLoading.value = false
  }
}

const onGoogleSuccess = async (tokenResponse) => {
  clearError()
  try {
    const accessToken = tokenResponse?.access_token
    if (!accessToken) {
      showError(t('modal.login.errors.generic'))
      return
    }
    const payload = await authApi.loginGoogle(accessToken)
    await auth.login(payload)
    await props.onSubmit?.({ provider: 'google' })
    await navigateTo(props.redirect)
  } catch (e) {
    const code = e?.message
    const map = {
      invalid_google_token: 'generic',
      google_email_not_verified: 'generic',
      customer_disabled: 'customer_disabled',
    }
    showError(t(`modal.login.errors.${map[code] || 'generic'}`))
  }
}

const onSignup = () => {
  close()
  upload.triggerUpload()
}

const googleTokenClient = shallowRef(null)

const onGoogleClick = () => googleTokenClient.value?.requestAccessToken()

watch(
  () => googleAuth.isAvailable.value,
  async (available) => {
    if (!available) return
    googleTokenClient.value = await googleAuth.initTokenClient(onGoogleSuccess)
  },
  { immediate: true },
)
</script>

<template>
  <div class="modal-overlay">
    <div class="modal-box">
      <div class="flex items-center justify-between gap-3 mb-1.5">
        <h3 class="font-black text-lg text-ink">
          {{ mode === 'login' ? $t('modal.login.title') : $t('modal.login.forgot_view.title') }}
        </h3>
        <button
          type="button"
          class="text-subtle hover:text-ink-soft focus:outline-none shrink-0"
          :aria-label="$t('modal.login.close')"
          @click="close"
        >
          <Icon name="material-symbols:close" class="text-xl" />
        </button>
      </div>
      <template v-if="mode === 'login'">
        <p class="text-sm text-muted mb-5 leading-relaxed">{{ $t('modal.login.desc') }}</p>
        <form @submit.prevent="onLogin">
          <input v-model="email" class="w-full px-4 py-3 border-[1.5px] border-line rounded-xl text-sm outline-none transition-colors duration-150 mb-2.5 focus:border-brand" type="email" placeholder="tu@email.com" autocomplete="email" />
          <input v-model="password" class="w-full px-4 py-3 border-[1.5px] border-line rounded-xl text-sm outline-none transition-colors duration-150 mb-1.5 focus:border-brand" type="password" :placeholder="$t('modal.login.password')" autocomplete="current-password" />
          <div class="text-right mb-2.5">
            <a class="text-xs font-semibold text-brand-text cursor-pointer hover:text-brand-text" @click="goForgot">{{ $t('modal.login.forgot') }}</a>
          </div>
          <Transition
            enter-active-class="transition duration-200 ease-out"
            enter-from-class="opacity-0 -translate-y-1"
            enter-to-class="opacity-100 translate-y-0"
            leave-active-class="transition duration-200 ease-in"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
          >
            <div v-if="error" role="alert" class="flex items-start gap-2 p-3 mb-2.5 rounded-xl border border-red-200 bg-red-50">
              <svg class="w-4 h-4 mt-0.5 shrink-0 text-red-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm0-12a1 1 0 0 1 1 1v4a1 1 0 1 1-2 0V7a1 1 0 0 1 1-1Zm0 9a1.25 1.25 0 1 0 0-2.5 1.25 1.25 0 0 0 0 2.5Z" clip-rule="evenodd"/></svg>
              <p class="text-xs font-semibold text-red-700 leading-relaxed">{{ error }}</p>
            </div>
          </Transition>
          <button class="w-full p-3.5 rounded-xl bg-grad-brand text-on-brand font-extrabold text-sm cursor-pointer mb-2.5 shadow-[0_4px_16px_rgb(var(--c-brand)/.3)] disabled:opacity-50 disabled:cursor-not-allowed" :disabled="loading" type="submit">{{ loading ? '…' : `${$t('modal.login.submit')} →` }}</button>
        </form>
        <div class="text-center text-xs text-subtle my-2 relative before:content-[''] before:absolute before:top-1/2 before:left-0 before:w-[42%] before:h-px before:bg-surface-sunken after:content-[''] after:absolute after:top-1/2 after:right-0 after:w-[42%] after:h-px after:bg-surface-sunken">{{ $t('modal.login.or') }}</div>
        <button
          v-show="googleAuth.isAvailable"
          type="button"
          class="w-full p-3.5 rounded-xl border-[1.5px] border-line text-ink-soft font-bold text-sm flex items-center justify-center gap-2.5 transition-colors duration-150 hover:border-brand"
          @click="onGoogleClick"
        >
          <svg width="18" height="18" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
          {{ $t('modal.login.google') }}
        </button>
        <p class="text-xs text-subtle text-center mt-4">
          {{ $t('modal.login.noAccount') }}
          <a class="text-brand-text font-semibold cursor-pointer" @click="onSignup">{{ $t('modal.login.startFree') }}</a>
        </p>
        <p class="text-[11px] text-subtle text-center mt-3 leading-relaxed [&_a]:text-inherit [&_a]:underline">
          {{ $t('modal.login.terms.before') }}
          <a href="/terms" target="_blank" rel="noopener noreferrer">{{ $t('modal.login.terms.tos') }}</a>
          {{ $t('modal.login.terms.middle') }}
          <a href="/privacy" target="_blank" rel="noopener noreferrer">{{ $t('modal.login.terms.privacy') }}</a>.
        </p>
      </template>

      <template v-else>
        <p class="text-sm text-muted mb-5 leading-relaxed">{{ $t('modal.login.forgot_view.desc') }}</p>
        <form @submit.prevent="onForgotSubmit">
          <input v-model="forgotEmail" class="w-full px-4 py-3 border-[1.5px] border-line rounded-xl text-sm outline-none transition-colors duration-150 mb-2.5 focus:border-brand" type="email" placeholder="tu@email.com" autocomplete="email" autofocus />
          <p v-if="forgotError" class="text-xs text-red-500 mb-2.5">{{ forgotError }}</p>
          <button class="w-full p-3.5 rounded-xl bg-grad-brand text-on-brand font-extrabold text-sm cursor-pointer mb-2.5 shadow-[0_4px_16px_rgb(var(--c-brand)/.3)] disabled:opacity-50 disabled:cursor-not-allowed" :disabled="forgotLoading" type="submit">{{ forgotLoading ? '…' : `${$t('modal.login.forgot_view.submit')} →` }}</button>
        </form>
        <button type="button" class="w-full p-3 text-xs font-semibold text-muted cursor-pointer hover:text-brand-text transition-colors duration-150" @click="goLogin">{{ $t('modal.login.forgot_view.back') }}</button>
      </template>
    </div>
  </div>
</template>
